import { Navigate } from "react-router-dom";

import { useMe } from "@/hooks/useMe";
import { DashboardPage } from "@/pages/DashboardPage";
import type { Ruolo } from "@/types/api";

/**
 * Landing della rotta `/`. I ruoli non ancora abilitati all'uso operativo
 * (cliente/viewer, commerciale) vengono reindirizzati a una pagina temporanea;
 * il ruolo logistica atterra sul **calendario dei ritiri**; tutti gli altri
 * vedono la dashboard direzionale (Operations Hub).
 *
 * Marina aveva una dashboard sua (`DashboardLogistica`, giugno 2026), tolta col
 * calendario: leggeva `m3.carico`, cioè il modello di M3 di allora, e sul mirror
 * di prod mostrava «0 camion oggi · 0 domani · 0 settimana» e un funnel
 * 32·0·0·0·0 — quattro blocchi che non dicevano più niente perché il lavoro si
 * era spostato sul tabellone. Il calendario è la stessa domanda («la mia
 * giornata») fatta ai dati veri.
 *
 * Vale anche con «Vedi come → Logistica»: l'header X-Vedi-Come è sempre
 * inviato, quindi `useMe()` riflette il ruolo impersonato.
 */
const RUOLI_NON_ABILITATI: ReadonlySet<Ruolo> = new Set<Ruolo>([
  "mp_viewer",
  "mp_commerciale",
]);

export function DashboardLanding() {
  const { data: me, isLoading } = useMe();

  // In attesa del profilo: niente redirect (evita flash); la pagina gestisce
  // i propri stati di caricamento.
  if (!isLoading && me) {
    if (RUOLI_NON_ABILITATI.has(me.ruolo)) {
      return <Navigate to="/non-disponibile" replace />;
    }
    // Redirect e non render diretto: la rotta deve essere quella vera, altrimenti
    // la barra non saprebbe quale voce evidenziare.
    if (me.ruolo === "mp_logistica") {
      return <Navigate to="/calendario" replace />;
    }
    // Dal 18-08 la direzione ha un modulo suo: atterra là, non su una dashboard
    // di riquadri. Stessa regola di Marina — si atterra dove si lavora.
    // ⚠️ La dashboard NON è stata cancellata: resta l'atterraggio di chi non ha
    // ancora un modulo proprio (staff, planning). Toglierla nello stesso commit
    // avrebbe lasciato senza atterraggio chi non ha il permesso del suo modulo —
    // e `mp_planning` non ha `modulo.M2`. Vedi `module_navigazione_permessi`:
    // mai cambiare accessi di nascosto.
    if (me.ruolo === "mp_presidenza" || me.ruolo === "mp_management") {
      return <Navigate to="/moduli/direzione" replace />;
    }
  }

  return <DashboardPage />;
}
